import React, { useContext, useEffect, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import { Badge, Box, Flex, Spinner } from "theme-ui";

import { Form, Formik } from "formik";
import * as Yup from "yup";
import { getAllApiKeys, adminRevokeApiKey, adminReactivateApiKey, adminCreateApiKey } from "api/ApiKeys";
import { getClients } from "api/Clients";
import { trackActivity } from "api/ActivityTracker";

import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import BasicModal from "components/ui/BasicModal";
import Span from "components/typography/Span";
import TableData from "components/ui/TableData";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { useToast } from "components/toast";
import { KeyIcon, QuestionMarkIcon, WarningIcon } from "components/svg";
import H3 from "components/typography/H3";
import Separator from "components/ui/Separator";
import UserContext from "context/User";
import P from "components/typography/P";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { ModalIcon, ModalTitleWrapper, ModalTitle, ModalSubtitle, EllipsisWrapper } from "../styled";
import { themevals } from "theme/themevals";

export default function ApiSettings() {
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshData, setRefreshData] = useState(false);
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [limitModal, setLimitModal] = useState<any>(false);
  const [viewModal, setViewModal] = useState(false);
  const [viewingKey, setViewingKey] = useState<any>(null);

  const MAX_ACTIVE_API_KEYS = 3;

  // ── Issue a key for a client (staff) ──
  const [createModal, setCreateModal] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState<any[]>([]);
  const [createClient, setCreateClient] = useState<any>(null);
  // "company" = website intake key: no client, accepted only by lead intake.
  const [keyScope, setKeyScope] = useState<"company" | "client">("company");

  useEffect(() => {
    if (!createModal || keyScope !== "client" || createClient || clientSearch.trim().length < 2) {
      setClientResults([]);
      return;
    }
    const t = setTimeout(async () => {
      const res: any = await getClients({ query: { search: clientSearch.trim(), filters: { type: 2 } } });
      const items = Array.isArray(res?.items || res) ? res?.items || res : [];
      setClientResults(items.slice(0, 8));
    }, 300);
    return () => clearTimeout(t);
  }, [clientSearch, createModal, createClient, keyScope]);

  const closeCreateModal = () => {
    setCreateModal(false);
    setKeyScope("company");
    setCreateClient(null);
    setClientSearch("");
    setClientResults([]);
  };

  const CreateSchema = Yup.object().shape({
    name: Yup.string().required("Name is required."),
    environment: Yup.string().oneOf(["production", "development"]).required(),
  });

  const submitCreate = async (values: any) => {
    if (keyScope === "client" && !createClient?.id) {
      toast.add("Choose a client first.", "var(--theme-ui-colors-danger)");
      return;
    }
    const res = await adminCreateApiKey({
      create: {
        ...(keyScope === "client" ? { clientId: createClient.id } : {}),
        name: values.name,
        environment: values.environment,
        allowedDomain: values.allowedDomain || undefined,
        description: values.description || undefined,
      },
    });
    if (res?.id) {
      toast.add("API key created.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "company_settings",
        action: "create_api_key",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "api_key",
        entityId: res.id,
        metadata: { name: values.name, environment: values.environment, scope: keyScope, clientName: createClient?.name ?? null },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      closeCreateModal();
      loadApiKeys();
      openViewModal(res); // show the new key so it can be copied straight away
    } else {
      toast.add(res?.message ? res.message : "Error creating API Key", "var(--theme-ui-colors-danger)");
    }
  };

  const columnHelper = createColumnHelper();

  const formatDate = (dateString: string): string => {
    return moment(dateString).format("MMM D, YYYY");
  };

  const openViewModal = (apiKey: any) => {
    setViewingKey(apiKey);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewingKey(null);
    setViewModal(false);
  };

  const getActiveApiKeysCountForClient = (clientId: string) => {
    return apiKeys.filter((key) => key.client?.id === clientId && key.isActive).length;
  };

  const confirmRevoke = async (apiKey: any) => {
    setConfirmSubmit(true);
    const res = await adminRevokeApiKey({ id: apiKey.id });
    if (res?.id) {
      toast.add("API key revoked.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "company_settings",
        action: "revoke_api_key",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "api_key",
        entityId: res?.id ?? null,
        metadata: {
          name: apiKey.name,
          clientName: apiKey.client?.name,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      loadApiKeys();
      setRefreshData((r) => !r);
    } else {
      toast.add(res?.message ? res.message : "Error revoking API Key", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const confirmReactivate = async (apiKey: any) => {
    setConfirmSubmit(true);
    const res = await adminReactivateApiKey({ id: apiKey.id });
    if (res?.id) {
      toast.add("API key reactivated.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "company_settings",
        action: "reactivate_api_key",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "api_key",
        entityId: res?.id ?? null,
        metadata: {
          name: apiKey.name,
          clientName: apiKey.client?.name,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      loadApiKeys();
      setRefreshData((r) => !r);
    } else {
      toast.add(res?.message ? res.message : "Error reactivating API Key", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const showRevokeConfirmation = (apiKey: any) => {
    setConfirmModal(
      <BasicModal
        title="Revoke API Key?"
        size="sm"
        cancel_click={() => {
          setConfirmModal(false);
        }}
        cancel_text="No, cancel"
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton
            type="button"
            scheme="danger"
            onClick={() => {
              confirmRevoke(apiKey);
            }}
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $loadingColor="#fff"
            keepText={true}
          >
            Revoke
          </BasicButton>
        }
      >
        <P textAlign="center" marginTop={0} marginBottom={0}>
          Are you sure you want to revoke this API key?
        </P>
      </BasicModal>
    );
  };

  const showLimitReachedModal = (clientName: string) => {
    setLimitModal(
      <BasicModal
        title="Limit Reached!"
        size="sm"
        cancel_click={() => setLimitModal(false)}
        cancel_text="Close"
        icon={<WarningIcon fill={"#818181"} height="30px" />}
      >
        <P textAlign="center" marginTop={0} marginBottom={0} lineHeight="22px">
          {clientName} may only have {MAX_ACTIVE_API_KEYS} Active API keys at one time. Revoke one of their active keys to reactivate this one.
        </P>
      </BasicModal>
    );
  };

  const showReactivateConfirmation = (apiKey: any) => {
    // Check if we've reached the limit for this client
    if (getActiveApiKeysCountForClient(apiKey.client?.id) >= MAX_ACTIVE_API_KEYS) {
      showLimitReachedModal(apiKey.client?.name || "This client");
      return;
    }
    setConfirmModal(
      <BasicModal
        title="Reactivate API Key?"
        size="sm"
        cancel_click={() => {
          setConfirmModal(false);
        }}
        cancel_text="No, cancel"
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton
            type="button"
            scheme="success"
            onClick={() => {
              confirmReactivate(apiKey);
            }}
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $loadingColor="#fff"
            keepText={true}
          >
            Reactivate
          </BasicButton>
        }
      >
        <P textAlign="center" marginTop={0} marginBottom={0}>
          Are you sure you want to reactivate this API key?
        </P>
      </BasicModal>
    );
  };

  const columns = [
    columnHelper.accessor("client.name", {
      id: "client",
      cell: (info: any) => (
        <Span color="#000" fontWeight="500">{info.getValue() || "Company key"}</Span>
      ),
      header: () => <span>Client</span>,
       meta: {
        style: { width: "200px", maxWidth:"200px" },
      },
    }),
    columnHelper.accessor("isActive", {
      id: "status",
      cell: (info: any) => (
        info.getValue() 
          ? <Badge as="span" variant="success">Active</Badge>
          : <Badge as="span" variant="danger">Revoked</Badge>
      ),
      header: () => <span>Status</span>,
      meta: {
        style: { width: "120px" },
      },
    }),
    columnHelper.accessor("allowedDomain", {
      id: "allowedDomain",
      cell: (info: any) => (
        <Span color="#818181">{info.getValue() || "-"}</Span>
      ),
      header: () => <span>Domain</span>,
    }),
    columnHelper.accessor("environment", {
      id: "environment",
      cell: (info: any) => (
        <Span color="#818181" style={{ textTransform: "capitalize" }}>{info.getValue()}</Span>
      ),
      header: () => <span>Environment</span>,
      meta: {
        style: { width: "120px" },
      },
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      cell: (info: any) => (
        <Span color="#818181">{formatDate(info.getValue())}</Span>
      ),
      header: () => <span>Date Created</span>,
      meta: {
        style: { width: "150px" },
      },
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const row = info.row.original;
        const menuItems = [
          { label: "View", onClick: () => openViewModal(row) },
          ...(row.isActive
            ? [
                {
                  label: "Revoke",
                  style: { color: themevals.colors.danger },
                  onClick: () => showRevokeConfirmation(row),
                },
              ]
            : [
                {
                  label: "Reactivate",
                  style: { color: themevals.colors.success },
                  onClick: () => showReactivateConfirmation(row),
                },
              ]),
        ];

        return (
          <EllipsisWrapper>
            <DropDownMenu
              toggleComponent={
                <EllipsisIcon fill={"#818181"} width="20px" height="20px" />
              }
              items={menuItems}
            />
          </EllipsisWrapper>
        );
      },
      header: () => <span></span>,
      meta: {
        style: { width: "50px" },
      },
    }),
  ];

  const loadApiKeys = async () => {
    setLoading(true);
    const res = await getAllApiKeys();
    setApiKeys(res?.items || []);
    setLoading(false);
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  return (
    <>
      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <H3 marginTop={"10px"} marginBottom={0} fontSize={"16px"} fontWeight={"600"} fontFamily={"Poppins"}>
          APIs
        </H3>
      </Box>
      <Separator />

      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        {loading ? (
          <Box sx={{ textAlign: "center", padding: "40px" }}>
            <Spinner size={25} strokeWidth={3} title="loading" />
          </Box>
        ) : apiKeys.length === 0 ? (
          /* Empty State */
          <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <P color="#818181" fontSize="14px" fontFamily="Poppins" marginTop={0} marginBottom={0}>
              There are currently no API keys associated with any clients.
            </P>
            <BasicButton scheme="secondaryOutline" onClick={() => setCreateModal(true)}>
              Create API Key
            </BasicButton>
          </Flex>
        ) : (
          /* Table with API Keys */
          <TableData
            title={""}
            api={getAllApiKeys}
            columns={columns}
            refresh={refreshData}
            enableSearch={false}
            enablePaging={false}
            actions={[
              <BasicButton key="create-api-key" scheme="secondaryOutline" onClick={() => setCreateModal(true)}>
                Create API Key
              </BasicButton>,
            ]}
          />
        )}
      </Box>

      {/* View API Key Modal */}
      <Modal isOpen={viewModal} toggle={closeViewModal} centered={true} size="sm">
        <ModalHeader toggle={closeViewModal}>
          <ModalIcon>
            <KeyIcon fill={"#000"} width="20px" />
          </ModalIcon>
          <ModalTitleWrapper>
            <ModalTitle>View API Key</ModalTitle>
            <ModalSubtitle>Copy your API key for authorized use of the API</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          <TextInput
            name="apiKey"
            label="Key"
            value={viewingKey?.apiKey || ""}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onChange={() => {}}
            readonly={true}
            autoComplete="off"
          />
          <P color="#818181" fontSize="12px" fontFamily="Poppins" marginTop="16px" marginBottom={0} lineHeight="18px">
            {viewingKey?.environment === "development"
              ? "This is a development key. Use it with the sandbox API at https://api-sandbox-jscrm.getmulch.com. Requests made with this key will not affect production data."
              : `This is a production key. Use it with the API at ${process.env.REACT_APP_BACKEND || 'https://api-jscrm.getmulch.com'}. Requests made with this key will affect live data.`}
          </P>
        </ModalBody>
        <ModalFooter>
          <BasicButton
            outline={true}
            type="button"
            onClick={closeViewModal}
            scheme="secondary"
          >
            Close
          </BasicButton>
        </ModalFooter>
      </Modal>

      {/* Create API Key Modal (staff, for a client) */}
      <Modal isOpen={createModal} toggle={closeCreateModal} centered={true} size="sm">
        <Formik
          initialValues={{ name: "", environment: "production", allowedDomain: "", description: "" }}
          onSubmit={submitCreate}
          validationSchema={CreateSchema}
        >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
            <Form noValidate autoComplete="off">
              <ModalHeader toggle={closeCreateModal}>
                <ModalIcon>
                  <KeyIcon fill={"#000"} width="20px" />
                </ModalIcon>
                <ModalTitleWrapper>
                  <ModalTitle>Create API Key</ModalTitle>
                  <ModalSubtitle>Company keys only work for website lead intake.</ModalSubtitle>
                </ModalTitleWrapper>
              </ModalHeader>
              <ModalBody>
                <SelectInput
                  name="keyScope"
                  label="Key type"
                  value={keyScope}
                  onChange={(val: any) => {
                    setKeyScope(val === "client" ? "client" : "company");
                    setCreateClient(null);
                  }}
                  options={[
                    { value: "company", label: "Company key (website lead intake)" },
                    { value: "client", label: "Client key (client widget and API)" },
                  ]}
                  $customStyles={{ marginBottom: "20px" }}
                />
                {keyScope === "company" ? null : createClient ? (
                  <Flex sx={{ justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <Span color="#000" fontWeight="500">{createClient.name}</Span>
                    <BasicButton type="button" scheme="secondaryOutline" onClick={() => setCreateClient(null)}>
                      Change
                    </BasicButton>
                  </Flex>
                ) : (
                  <Box sx={{ marginBottom: "20px" }}>
                    <TextInput
                      name="clientSearch"
                      label="Client"
                      placeholder="Search client companies"
                      value={clientSearch}
                      onChange={(val: any) => setClientSearch(val)}
                      autoComplete="off"
                    />
                    {clientResults.map((c: any) => (
                      <Box
                        key={c.id}
                        onClick={() => {
                          setCreateClient({ id: c.id, name: c.name });
                          setClientResults([]);
                        }}
                        sx={{ padding: "8px 10px", cursor: "pointer", fontSize: "13px", "&:hover": { backgroundColor: "#F0F7FF" } }}
                      >
                        {c.name}
                      </Box>
                    ))}
                  </Box>
                )}
                <TextInput
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={(val: any) => setFieldValue("name", val)}
                  autoComplete="off"
                  $errors={errors.name && submitCount > 0 ? errors.name : null}
                />
                <SelectInput
                  name="environment"
                  label="Environment"
                  value={values.environment}
                  onChange={(val: any) => setFieldValue("environment", val)}
                  options={[
                    { value: "production", label: "Production" },
                    { value: "development", label: "Development" },
                  ]}
                  $customStyles={{ marginBottom: "30px" }}
                />
                <TextInput
                  name="allowedDomain"
                  label="Allowed Domain"
                  value={values.allowedDomain}
                  onChange={(val: any) => setFieldValue("allowedDomain", val)}
                  autoComplete="off"
                  placeholder="e.g. example.com"
                />
                <TextInput
                  type="textarea"
                  name="description"
                  label="Description"
                  value={values.description}
                  onChange={(val: any) => setFieldValue("description", val)}
                  autoComplete="off"
                  $customStyles={{ marginBottom: "30px" }}
                />
              </ModalBody>
              <ModalFooter>
                <BasicButton outline={true} type="button" onClick={closeCreateModal} scheme="secondary" styles={{ margin: ".25rem 10px .25rem 0" }}>
                  Cancel
                </BasicButton>
                <BasicButton type="submit" $submitting={isSubmitting} $loadingColor="#fff" keepText={true}>
                  Create Key
                </BasicButton>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Revoke/Reactivate Confirmation Modal */}
      {confirmModal}

      {/* Limit Reached Modal */}
      {limitModal}
    </>
  );
}
